<template>
  <div class="account-settings-page">
    <div class="page-container">
      <header class="page-header">
        <h1 class="page-title">Account Settings</h1>
        <p class="page-subtitle">Manage your account information and preferences</p>
      </header>

      <!-- Email Verification Banner -->
      <div v-if="!authStore.emailVerified" class="verification-banner">
        <div class="verification-content">
          <div class="verification-text">
            <strong>Verify your email</strong>
            <p>Check your inbox for a verification link.</p>
          </div>
          <button @click="resendVerification" :disabled="resendingVerification" class="resend-button">
            {{ resendingVerification ? 'Sending...' : 'Resend Email' }}
          </button>
        </div>
        <p v-if="verificationSent" class="verification-success">Verification email sent!</p>
        <p v-if="verificationError" class="verification-error">{{ verificationError }}</p>
      </div>

      <!-- Profile Settings -->
      <div class="settings-section">
        <h2 class="section-title">Profile Information</h2>
        <form @submit.prevent="saveProfile" class="settings-form">
          <div class="form-group">
            <label class="form-label">Email Address</label>
            <p class="form-static">{{ authStore.displayEmail }}</p>
            <p class="form-hint">To change your email, contact support.</p>
          </div>

          <div class="form-group">
            <label class="form-label">Name</label>
            <input v-model="form.name" type="text" class="form-input" placeholder="Your name" />
          </div>

          <div class="form-group">
            <label class="form-label">Phone (optional)</label>
            <input v-model="form.phone" type="tel" class="form-input" placeholder="+1 (555) 123-4567" />
            <p class="form-hint">We'll only use this for important account notifications.</p>
          </div>

          <div class="form-actions">
            <button type="submit" class="save-button" :disabled="saving || !hasChanges">
              {{ saving ? 'Saving...' : 'Save Changes' }}
            </button>
            <button v-if="hasChanges" type="button" @click="resetForm" class="cancel-button">Cancel</button>
          </div>

          <p v-if="saveError" class="error-message">{{ saveError }}</p>
          <p v-if="saveSuccess" class="success-message">Profile updated successfully!</p>
        </form>
      </div>

      <!-- Danger Zone -->
      <div class="settings-section danger-zone">
        <h2 class="section-title">Delete Account</h2>
        <p class="section-description">
          Closes your account and signs you out. We hold onto it for 30 days in case you change your mind.
        </p>
        <button @click="showDeleteConfirmation = true" class="delete-button">Delete My Account</button>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <transition name="modal">
      <div v-if="showDeleteConfirmation" class="modal-overlay" @click="closeDeleteConfirmation">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>Delete Account?</h3>
            <button @click="closeDeleteConfirmation" class="modal-close">×</button>
          </div>
          <div class="modal-body">
            <p>This signs you out and closes your account.</p>
            <p>
              Change your mind within 30 days? Email
              <a href="mailto:support@twig.io" class="support-link">support@twig.io</a> and we'll bring it back. After
              that, it's gone for good.
            </p>
            <label class="form-label" for="delete-confirmation">Type DELETE to confirm</label>
            <input
              id="delete-confirmation"
              v-model="deleteConfirmationText"
              type="text"
              class="form-input"
              autocomplete="off"
              spellcheck="false"
            />
          </div>
          <div class="modal-footer">
            <button @click="closeDeleteConfirmation" class="modal-cancel">Cancel</button>
            <button @click="deleteAccount" :disabled="deleting || !deleteConfirmed" class="modal-delete">
              {{ deleting ? 'Deleting...' : 'Yes, Delete My Account' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { fetchWithAuth } from '../services/api';
import { useAuthStore } from '../stores/auth';

const router = useRouter();
const authStore = useAuthStore();

const form = ref({
  name: '',
  phone: '',
});

const originalForm = ref({
  name: '',
  phone: '',
});

const saving = ref(false);
const saveError = ref('');
const saveSuccess = ref(false);
const showDeleteConfirmation = ref(false);
const deleteConfirmationText = ref('');
const deleteConfirmed = computed(() => deleteConfirmationText.value.trim() === 'DELETE');
const deleting = ref(false);
const resendingVerification = ref(false);
const verificationSent = ref(false);
const verificationError = ref('');

const hasChanges = computed(() => {
  return form.value.name !== originalForm.value.name || form.value.phone !== originalForm.value.phone;
});

async function loadProfile() {
  try {
    const response = await fetchWithAuth(`/api/users/${authStore.uid}`);
    if (response.ok) {
      const data = await response.json();
      form.value = {
        name: data.name || '',
        phone: data.phone || '',
      };
      originalForm.value = { ...form.value };
    }
  } catch (err) {
    console.error('Failed to load profile:', err);
  }
}

function resetForm() {
  form.value = { ...originalForm.value };
  saveError.value = '';
  saveSuccess.value = false;
}

async function saveProfile() {
  saveError.value = '';
  saveSuccess.value = false;
  saving.value = true;

  try {
    const response = await fetchWithAuth(`/api/users/${authStore.uid}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form.value),
    });

    if (!response.ok) {
      throw new Error('Failed to update profile');
    }

    originalForm.value = { ...form.value };
    saveSuccess.value = true;

    setTimeout(() => {
      saveSuccess.value = false;
    }, 3000);
  } catch (err) {
    saveError.value = 'Failed to update profile. Please try again.';
    console.error('Save profile error:', err);
  } finally {
    saving.value = false;
  }
}

function closeDeleteConfirmation() {
  showDeleteConfirmation.value = false;
  deleteConfirmationText.value = '';
}

async function deleteAccount() {
  if (!deleteConfirmed.value) return;
  deleting.value = true;

  try {
    const response = await fetchWithAuth(`/api/users/${authStore.uid}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error('Failed to delete account');
    }

    // Sign out and redirect
    await authStore.signOut();
    router.push('/auth');
  } catch (err) {
    saveError.value = 'Failed to delete account. Please try again.';
    console.error('Delete account error:', err);
  } finally {
    deleting.value = false;
    closeDeleteConfirmation();
  }
}

async function resendVerification() {
  resendingVerification.value = true;
  verificationSent.value = false;
  verificationError.value = '';

  try {
    await authStore.resendVerificationEmail();
    verificationSent.value = true;

    setTimeout(() => {
      verificationSent.value = false;
    }, 5000);
  } catch (err) {
    verificationError.value = 'Failed to send verification email. Try again.';
    console.error('Resend verification error:', err);
  } finally {
    resendingVerification.value = false;
  }
}

onMounted(() => {
  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth', query: { redirect: '/account' } });
  } else {
    loadProfile();
  }
});
</script>

<style scoped>
.account-settings-page {
  flex: 1;
  background: #f7fafc;
}

.page-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
}

.page-header {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2.25rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1.125rem;
  color: #718096;
}

/* Email Verification Banner */
.verification-banner {
  background: #fef5e7;
  border: 1px solid #f9e79f;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.verification-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.verification-text {
  flex: 1;
  min-width: 250px;
}

.verification-text strong {
  display: block;
  font-size: 1rem;
  font-weight: 600;
  color: #d68910;
  margin-bottom: 0.25rem;
}

.verification-text p {
  font-size: 0.875rem;
  color: #7d6608;
  margin: 0;
}

.resend-button {
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #d68910;
  background: white;
  border: 1px solid #d68910;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  white-space: nowrap;
}

.resend-button:hover:not(:disabled) {
  background: #fef5e7;
  transform: translateY(-1px);
}

.resend-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.verification-success {
  color: #38a169;
  font-size: 0.875rem;
  margin-top: 1rem;
  margin-bottom: 0;
  font-weight: 500;
}

.verification-error {
  color: #e53e3e;
  font-size: 0.875rem;
  margin-top: 1rem;
  margin-bottom: 0;
  font-weight: 500;
}

/* Settings Sections */
.settings-section {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.section-description {
  color: #718096;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

/* Form Styles */
.settings-form {
  margin-top: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 0.5rem;
}

.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  transition: border-color 0.2s;
  font-family: inherit;
}

.form-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px var(--color-accent-light);
}

.form-static {
  font-size: 1rem;
  color: #4a5568;
  margin: 0;
}

.form-hint {
  font-size: 0.75rem;
  color: #a0aec0;
  margin-top: 0.5rem;
}

.form-hint.warning {
  color: #d69e2e;
}

.form-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.save-button {
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  background: var(--color-accent);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.save-button:hover:not(:disabled) {
  background: var(--color-accent-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(97, 174, 212, 0.3);
}

.save-button:disabled {
  background: #a0aec0;
  cursor: not-allowed;
  transform: none;
}

.cancel-button {
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.cancel-button:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.error-message {
  color: #e53e3e;
  font-size: 0.875rem;
  margin-top: 1rem;
  font-weight: 500;
}

.success-message {
  color: #38a169;
  font-size: 0.875rem;
  margin-top: 1rem;
  font-weight: 500;
}

/* Danger Zone */
.danger-zone {
  border-color: #feb2b2;
}

.delete-button {
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  background: #e53e3e;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.delete-button:hover {
  background: #c53030;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(229, 62, 62, 0.3);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 100;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
}

.modal-close {
  background: none;
  border: none;
  font-size: 2rem;
  color: #a0aec0;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #f7fafc;
  color: #4a5568;
}

.modal-body {
  padding: 1.5rem;
}

.modal-body p {
  color: #4a5568;
  margin-bottom: 1rem;
}

.support-link {
  color: var(--color-accent);
  font-weight: 600;
}

.modal-footer {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e2e8f0;
  justify-content: flex-end;
}

.modal-cancel {
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.modal-cancel:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.modal-delete {
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  background: #e53e3e;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.modal-delete:hover:not(:disabled) {
  background: #c53030;
}

.modal-delete:disabled {
  background: #fc8181;
  cursor: not-allowed;
}

/* Modal Transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease-out;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform 0.2s ease-out;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: scale(0.95);
}
</style>
